import { X } from "lucide-react";
import { Button } from "@/components/Base/Button";
import { Tabs, TabsList, TabsTrigger } from "@/components/Base/Tabs";
import { AuditStatus } from "@/lib/shopConfigAudit";
import { AuditFilter, ShopSummary, Verdict } from "@/lib/shopConfigAuditView";
import { cn } from "@/lib/utils";
import { STATUS_STYLES } from "./statusStyles";

const FILTERS: { value: AuditFilter; label: string }[] = [
  { value: "issues", label: "Issues" },
  { value: "missing", label: "Not set" },
  { value: "drift", label: "Drifted" },
  { value: "all", label: "All" },
];

const TILES: { status: AuditStatus; label: string }[] = [
  { status: "missing", label: "Not set" },
  { status: "drift", label: "Drifted" },
  { status: "match", label: "Matching" },
];

const VERDICT_STATUS: Record<Verdict, AuditStatus> = {
  incomplete: "missing",
  customized: "drift",
  clean: "match",
};

const settingsWord = (n: number) => (n === 1 ? "setting was" : "settings were");

const LISTS_UNAVAILABLE_CAVEAT =
  "Tags and automations could not be loaded; this verdict covers settings only.";

const VerdictBlock = ({
  summary,
  listsUnavailable,
}: {
  summary: ShopSummary;
  listsUnavailable?: boolean;
}) => {
  const title = {
    incomplete: "Setup incomplete",
    customized: "Customized",
    clean: "Matches defaults",
  }[summary.verdict];
  const description =
    summary.verdict === "incomplete"
      ? `${summary.missing} ${settingsWord(summary.missing)} never set on this shop. Likely missed during onboarding.`
      : summary.verdict === "customized"
        ? `Everything is set up. ${summary.drift} ${settingsWord(summary.drift)} changed from defaults.`
        : null;
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={cn(
          "text-sm font-semibold",
          STATUS_STYLES[VERDICT_STATUS[summary.verdict]].tileValue
        )}
      >
        {title}
      </span>
      {description && (
        <span className="text-[13px] text-muted-foreground">{description}</span>
      )}
      {listsUnavailable && (
        <span className="text-[13px] text-muted-foreground">
          {LISTS_UNAVAILABLE_CAVEAT}
        </span>
      )}
    </div>
  );
};

interface ShopConfigAuditPanelHeaderProps {
  shopName: string;
  summary: ShopSummary;
  // True when the tags/automations fetch failed: the verdict then covers
  // settings only, and says so.
  listsUnavailable?: boolean;
  filter: AuditFilter;
  onFilterChange: (filter: AuditFilter) => void;
  onClose: () => void;
}

export const ShopConfigAuditPanelHeader = ({
  shopName,
  summary,
  listsUnavailable,
  filter,
  onFilterChange,
  onClose,
}: ShopConfigAuditPanelHeaderProps) => (
  <div className="sticky top-0 z-10 flex flex-col gap-4 border-b border-border bg-white px-6 pb-4 pt-6 text-foreground">
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-1.5">
        <h2 className="text-xl font-semibold text-foreground">{shopName}</h2>
        <VerdictBlock summary={summary} listsUnavailable={listsUnavailable} />
      </div>
      <Button
        variant="ghost-primary"
        size="icon"
        aria-label="Close panel"
        onClick={onClose}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
    <div className="grid grid-cols-3 gap-2">
      {TILES.map(({ status, label }) => (
        <div
          key={status}
          className={cn(
            "flex flex-col gap-0.5 rounded-lg px-3 py-2.5",
            STATUS_STYLES[status].tile
          )}
        >
          <span
            data-testid={`count-${status}`}
            className={cn(
              "text-[22px] font-bold leading-[1.1]",
              STATUS_STYLES[status].tileValue
            )}
          >
            {summary[status]}
          </span>
          <span className={cn("text-xs", STATUS_STYLES[status].tileLabel)}>
            {label}
          </span>
        </div>
      ))}
    </div>
    <Tabs
      value={filter}
      onValueChange={(value) => onFilterChange(value as AuditFilter)}
    >
      <TabsList variant="muted" className="w-full">
        {FILTERS.map(({ value, label }) => (
          <TabsTrigger key={value} value={value} className="flex-1">
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  </div>
);
